import LogoutIcon from '@mui/icons-material/Logout'
import { Box, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography, alpha } from '@mui/material'
import { FC, MouseEvent, useState } from 'react'
import { useGetIdentity, useLogout, useTranslate } from 'react-admin'
import { UserAvatar } from '../components/ui'
import { getUserName } from '../utils/userDetails'
import { border } from './theme/border'
import { themeColor, xxllncColor } from './theme/color'

// Avatar with initials; opens a small menu with the user's name and sign out.
export const UserMenu: FC = () => {
  const translate = useTranslate()
  const logout = useLogout()
  const { identity } = useGetIdentity()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  // Auth0 profile name, else the name claim of the access token.
  const name = (identity?.fullName || getUserName() || '').trim()

  return (
    <>
      <Tooltip title={translate('ui.accountMenu')}>
        <IconButton
          aria-label={translate('ui.accountMenu')}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={(event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
          size="small"
          sx={{
            'p': 0.25,
            'ml': 0.5,
            'borderRadius': '50%',
            'transition': 'box-shadow 0.15s ease',
            '&:hover': { boxShadow: `0 0 0 2px ${alpha(themeColor.blue, 0.25)}` },
            ...(open ? { boxShadow: `0 0 0 2px ${alpha(themeColor.blue, 0.4)}` } : {}),
          }}
        >
          <UserAvatar name={name} size={30} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { elevation: 0, sx: { mt: 1, minWidth: 220, borderRadius: '14px', overflow: 'hidden' } } }}
      >
        {name && (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, mb: 0.5,
              borderBottom: `${border.thin} solid ${border.light}`,
            }}
          >
            <UserAvatar name={name} size={34} />
            <Typography
              sx={{
                fontWeight: 600, fontSize: 14, color: themeColor.slate,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {name}
            </Typography>
          </Box>
        )}
        <MenuItem
          onClick={() => { setAnchorEl(null); void logout() }}
          sx={{ color: `${xxllncColor.red100} !important`, fontWeight: 500 }}
        >
          <ListItemIcon sx={{ minWidth: 34 }}>
            <LogoutIcon sx={{ fontSize: 19, fill: `${xxllncColor.red100} !important` }} />
          </ListItemIcon>
          {translate('ra.auth.logout')}
        </MenuItem>
      </Menu>
    </>
  )
}
